import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { PlusCircle, Edit, Trash2, ArrowUpDown, Palette } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext'; // Assuming you have a ThemeContext
// Importe os tipos gerados pelo Supabase se você os tiver em um local conhecido
// import { Tables } from '@/types/supabase'; // Exemplo, ajuste o caminho

// type SupabaseHeroBannerRow = Tables<"hero_banners">["Row"]; // Exemplo

export interface HeroBanner {
  id?: string;
  title: string;
  subtitle: string;
  image_url: string;
  cta1_text: string;
  cta1_link: string;
  cta2_text: string;
  cta2_link: string;
  is_active: boolean;
  sort_order: number;
  background_color?: string | null;
  visibility: 'all' | 'logged_in' | 'logged_out';
  created_at?: string;
  updated_at?: string;
}

const DEFAULT_BANNER_COLOR = '#6366F1'; // Indigo-500 como padrão
const DEFAULT_VISIBILITY = 'all';

const initialBannerForm: HeroBanner = {
  title: '',
  subtitle: '',
  image_url: '',
  cta1_text: '',
  cta1_link: '#',
  cta2_text: '',
  cta2_link: '#',
  is_active: true,
  sort_order: 0,
  background_color: DEFAULT_BANNER_COLOR,
  visibility: DEFAULT_VISIBILITY,
};

const HeroBannersAdminContent: React.FC = () => {
  const { theme } = useTheme();
  const [banners, setBanners] = useState<HeroBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<HeroBanner | null>(null);
  const [bannerForm, setBannerForm] = useState<HeroBanner>(initialBannerForm);

  const loadBanners = useCallback(async () => {
    setLoading(true);
    try {
      // Especifica o tipo esperado para `data` para melhor inferência e segurança
      const { data, error } = await supabase
        .from('hero_banners')
        .select('*')
        .order('sort_order', { ascending: true });
        
      if (error) throw error;

      // Processa os dados garantindo que as propriedades obrigatórias da UI tenham valores
      const processedData: HeroBanner[] = data?.map(banner => ({
        ...banner,
        id: banner.id || undefined, // Garante que id seja string ou undefined
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        image_url: banner.image_url || '',
        cta1_text: banner.cta1_text || '',
        cta1_link: banner.cta1_link || '',
        cta2_text: banner.cta2_text || '',
        cta2_link: banner.cta2_link || '',
        is_active: typeof banner.is_active === 'boolean' ? banner.is_active : true,
        sort_order: banner.sort_order === null || banner.sort_order === undefined ? 0 : Number(banner.sort_order),
        background_color: banner.background_color || DEFAULT_BANNER_COLOR,
        visibility: banner.visibility || DEFAULT_VISIBILITY,
        created_at: banner.created_at || undefined,
        updated_at: banner.updated_at || undefined,
      })) || [];
      setBanners(processedData);
    } catch (error: any) {
      toast.error(`Erro ao carregar banners: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBannerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked: boolean | 'indeterminate') => {
    if (typeof checked === 'boolean') {
      setBannerForm(prev => ({ ...prev, is_active: checked }));
    }
  };
  
  const handleSortOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBannerForm(prev => ({ ...prev, sort_order: parseInt(e.target.value, 10) || 0 }));
  };

  const handleEdit = (banner: HeroBanner) => {
    setEditingBanner(banner);
    // Garante que o formulário de edição use os fallbacks se necessário
    setBannerForm({
      ...initialBannerForm, // Começa com todos os defaults, incluindo background_color
      ...banner, // Sobrescreve com os valores do banner
      is_active: typeof banner.is_active === 'boolean' ? banner.is_active : true,
      background_color: banner.background_color || DEFAULT_BANNER_COLOR,
      visibility: banner.visibility || DEFAULT_VISIBILITY,
    });
    setShowFormModal(true);
  };

  const handleAddNew = () => {
    setEditingBanner(null);
    setBannerForm(initialBannerForm); 
    setShowFormModal(true);
  };

  const handleDelete = async (bannerId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este banner?')) return;
    try {
      const { error } = await supabase.from('hero_banners').delete().eq('id', bannerId);
      if (error) throw error;
      toast.success('Banner excluído com sucesso!');
      loadBanners();
    } catch (error: any) {
      toast.error(`Erro ao excluir banner: ${error.message}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBanner?.id) {
        // Update
        const { error } = await supabase
          .from('hero_banners')
          .update({ ...bannerForm, updated_at: new Date().toISOString() })
          .eq('id', editingBanner.id);
        if (error) throw error;
        toast.success('Banner atualizado com sucesso!');
      } else {
        // Create
        const { error } = await supabase.from('hero_banners').insert(bannerForm);
        if (error) throw error;
        toast.success('Banner criado com sucesso!');
      }
      setShowFormModal(false);
      loadBanners();
    } catch (error: any) {
      toast.error(`Erro ao salvar banner: ${error.message}`);
    }
  };
  
  // Implement drag and drop for sort_order later if needed
  // For now, manual sort_order input

  return (
    <div className={`p-4 rounded-lg shadow ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>Gerenciar Banners da Home</h2>
        <Button onClick={handleAddNew} className="flex items-center gap-2">
          <PlusCircle size={18} /> Adicionar Novo Banner
        </Button>
      </div>

      {loading ? (
        <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Carregando banners...</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Ordem</TableHead>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Título</TableHead>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Subtítulo</TableHead>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Imagem</TableHead>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Cor Fundo</TableHead>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Visibilidade</TableHead>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Ativo</TableHead>
              <TableHead className={`${theme === 'dark' ? 'text-gray-300' : ''}`}>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {banners.map((banner) => (
              <TableRow key={banner.id} className={`${theme === 'dark' ? 'border-gray-700' : ''}`}>
                <TableCell className={`${theme === 'dark' ? 'text-gray-200' : ''}`}>{banner.sort_order}</TableCell>
                <TableCell className={`${theme === 'dark' ? 'text-gray-200' : ''}`}>{banner.title}</TableCell>
                <TableCell className={`${theme === 'dark' ? 'text-gray-200' : ''}`}>{banner.subtitle}</TableCell>
                <TableCell>
                  <img src={banner.image_url} alt={banner.title} className="h-10 w-auto rounded"/>
                </TableCell>
                <TableCell>
                  <div style={{ backgroundColor: banner.background_color }} className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600"></div>
                </TableCell>
                <TableCell>
                  {banner.visibility === 'all' && 'Todos'}
                  {banner.visibility === 'logged_in' && 'Logados'}
                  {banner.visibility === 'logged_out' && 'Deslogados'}
                </TableCell>
                <TableCell>
                  <Checkbox checked={banner.is_active} disabled className="border-gray-500"/>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(banner)} className={`${theme === 'dark' ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : ''}`}>
                      <Edit size={16} />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(banner.id!)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      
      {banners.length === 0 && !loading && (
        <p className={`text-center mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Nenhum banner encontrado.</p>
      )}

      <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
        <DialogContent className={`max-w-2xl ${theme === 'dark' ? 'dark bg-gray-800 border-gray-700 text-gray-100' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle>{editingBanner ? 'Editar Banner' : 'Adicionar Novo Banner'}</DialogTitle>
            <DialogDescription>
              Preencha os detalhes do banner.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <label htmlFor="title" className="block text-sm font-medium mb-1">Título *</label>
              <Input id="title" name="title" value={bannerForm.title} onChange={handleInputChange} required 
                     className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <div>
              <label htmlFor="subtitle" className="block text-sm font-medium mb-1">Subtítulo *</label>
              <Textarea id="subtitle" name="subtitle" value={bannerForm.subtitle} onChange={handleInputChange} required 
                        className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <div>
              <label htmlFor="image_url" className="block text-sm font-medium mb-1">URL da Imagem *</label>
              <Input id="image_url" name="image_url" value={bannerForm.image_url} onChange={handleInputChange} required 
                     className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <div>
              <label htmlFor="cta1_text" className="block text-sm font-medium mb-1">Texto do Botão 1 *</label>
              <Input id="cta1_text" name="cta1_text" value={bannerForm.cta1_text} onChange={handleInputChange} required 
                     className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <div>
              <label htmlFor="cta1_link" className="block text-sm font-medium mb-1">Link do Botão 1 *</label>
              <Input id="cta1_link" name="cta1_link" value={bannerForm.cta1_link} onChange={handleInputChange} required 
                     className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <div>
              <label htmlFor="cta2_text" className="block text-sm font-medium mb-1">Texto do Botão 2 *</label>
              <Input id="cta2_text" name="cta2_text" value={bannerForm.cta2_text} onChange={handleInputChange} required 
                     className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <div>
              <label htmlFor="cta2_link" className="block text-sm font-medium mb-1">Link do Botão 2 *</label>
              <Input id="cta2_link" name="cta2_link" value={bannerForm.cta2_link} onChange={handleInputChange} required 
                     className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <div className="mb-4">
              <label htmlFor="visibility" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Visibilidade</label>
              <select
                id="visibility"
                name="visibility"
                value={bannerForm.visibility}
                onChange={handleInputChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="all">Todos (Logados e Deslogados)</option>
                <option value="logged_in">Apenas Logados</option>
                <option value="logged_out">Apenas Deslogados</option>
              </select>
            </div>
            <div>
              <label htmlFor="background_color" className="block text-sm font-medium mb-1">Cor de Fundo (Hex)</label>
              <div className="flex items-center gap-2">
                <Input id="background_color" name="background_color" type="text" value={bannerForm.background_color} onChange={handleInputChange} required 
                       className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''} w-full`}/>
                <input type="color" value={bannerForm.background_color} onChange={(e) => setBannerForm(prev => ({...prev, background_color: e.target.value}))} 
                       className="h-10 w-12 p-0 border-none rounded cursor-pointer ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}"/>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="is_active" checked={bannerForm.is_active} onCheckedChange={handleCheckboxChange} 
                          className={`${theme === 'dark' ? 'border-gray-600 data-[state=checked]:bg-indigo-500' : ''}`}/>
              <label htmlFor="is_active" className="text-sm font-medium">Ativo</label>
            </div>
            <div>
              <label htmlFor="sort_order" className="block text-sm font-medium mb-1">Ordem de Exibição</label>
              <Input id="sort_order" name="sort_order" type="number" value={bannerForm.sort_order} onChange={handleSortOrderChange} 
                     className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}/>
            </div>
            <DialogFooter className="sm:justify-end pt-4">
              <DialogClose asChild>
                <Button type="button" variant="outline" className={`${theme === 'dark' ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : ''}`}>
                  Cancelar
                </Button>
              </DialogClose>
              <Button type="submit">{editingBanner ? 'Salvar Alterações' : 'Criar Banner'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeroBannersAdminContent; 